import { Box, CircularProgress, Grid } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import BarraBuscarLiga from '../components/buscadores/BarraBuscarLiga';
import { ContainerApp, PaperApp } from '../components/custom/CustomComponents';
import Clasificacion from '../components/ligas/Clasificacion';
import Jornada from '../components/ligas/Jornada';
import ListaJugadoresLiga from '../components/ligas/ListaJugadoresLiga';
import MaximosGoleadores from '../components/ligas/MaximosGoleadores';
import { Context } from '../service/Context';

/**
 * Pagina de una liga.
 */
const Liga = () => {
  const {
    obtenerUltimaJornada,
    obtenerTotalJornadas,
    obtenerPartidosJornada,
    obtenerDatosLiga,
    obtenerClasificacion,
    obtenerMaximosGoleadores,
    obtenerJugadoresPromocionados,
    obtenerJugadoresGuardados,
    ligasCache,
    setLigaCacheItem,
  } = useContext(Context);

  const { routeIdLiga } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (routeIdLiga === undefined) {
      navigate('/home');
    }
  }, [routeIdLiga, navigate]);

  const cachedLiga = ligasCache?.[routeIdLiga];

  const [datosLiga, setDatosLiga] = useState(cachedLiga?.datosLiga);
  const [partidosJornada, setPartidosJornada] = useState(cachedLiga?.partidosJornada);
  const [ultimaJornada, setUltimaJornada] = useState(cachedLiga?.ultimaJornada);
  const [totalJornadas, setTotalJornadas] = useState(cachedLiga?.totalJornadas);
  const [clasificacion, setClasificacion] = useState(cachedLiga?.clasificacion);
  const [maximosGoleadores, setMaximosGoleadores] = useState(cachedLiga?.maximosGoleadores);
  const [jugadoresPromocionados, setJugadoresPromocionados] = useState(cachedLiga?.jugadoresPromocionados);
  const [jugadoresGuardados, setJugadoresGuardados] = useState(cachedLiga?.jugadoresGuardados);

  // Estados de carga independientes por componente
  const [loadingDatosLiga, setLoadingDatosLiga] = useState(!cachedLiga?.datosLiga);
  const [loadingClasificacion, setLoadingClasificacion] = useState(!cachedLiga?.clasificacion);
  const [loadingJornada, setLoadingJornada] = useState(!cachedLiga?.partidosJornada);
  const [loadingGoleadores, setLoadingGoleadores] = useState(!cachedLiga?.maximosGoleadores);
  const [loadingPromocionados, setLoadingPromocionados] = useState(!cachedLiga?.jugadoresPromocionados);
  const [loadingGuardados, setLoadingGuardados] = useState(!cachedLiga?.jugadoresGuardados);

  // Sincronizar estado inmediatamente durante render al cambiar de liga en la URL
  const [prevRouteId, setPrevRouteId] = useState(routeIdLiga);
  if (prevRouteId !== routeIdLiga) {
    setPrevRouteId(routeIdLiga);
    const cached = ligasCache?.[routeIdLiga];
    if (cached?.datosLiga) {
      setDatosLiga(cached.datosLiga);
      setLoadingDatosLiga(false);
    } else {
      setLoadingDatosLiga(true);
    }

    setPartidosJornada(cached?.partidosJornada);
    setUltimaJornada(cached?.ultimaJornada);
    setTotalJornadas(cached?.totalJornadas);
    setClasificacion(cached?.clasificacion);
    setMaximosGoleadores(cached?.maximosGoleadores);
    setJugadoresPromocionados(cached?.jugadoresPromocionados);
    setJugadoresGuardados(cached?.jugadoresGuardados);

    setLoadingClasificacion(!cached?.clasificacion);
    setLoadingJornada(!cached?.partidosJornada);
    setLoadingGoleadores(!cached?.maximosGoleadores);
    setLoadingPromocionados(!cached?.jugadoresPromocionados);
    setLoadingGuardados(!cached?.jugadoresGuardados);
  }

  // Carga asíncrona desacoplada de cada módulo
  useEffect(() => {
    let cancelada = false;

    if (!routeIdLiga) {
      return;
    }

    const cached = ligasCache?.[routeIdLiga];
    setLoadingDatosLiga(!cached?.datosLiga);
    setLoadingClasificacion(!cached?.clasificacion);
    setLoadingJornada(!cached?.partidosJornada);
    setLoadingGoleadores(!cached?.maximosGoleadores);
    setLoadingPromocionados(!cached?.jugadoresPromocionados);
    setLoadingGuardados(!cached?.jugadoresGuardados);

    // 1. Datos de la liga
    obtenerDatosLiga(routeIdLiga)
      .then((res) => {
        if (!cancelada && res) {
          setDatosLiga(res);
          setLoadingDatosLiga(false);
          setLigaCacheItem(routeIdLiga, { datosLiga: res });
        }
      })
      .catch((err) => {
        console.error('Error al cargar datos de liga:', err);
        if (!cancelada) {
          setLoadingDatosLiga(false);
        }
      });

    // 2. Clasificación
    obtenerClasificacion(routeIdLiga)
      .then((res) => {
        if (!cancelada && res) {
          setClasificacion(res);
          setLoadingClasificacion(false);
          setLigaCacheItem(routeIdLiga, { clasificacion: res });
        }
      })
      .catch((err) => {
        console.error('Error al cargar clasificación:', err);
        if (!cancelada) {
          setLoadingClasificacion(false);
        }
      });

    // 3. Jornada (última jornada, total y partidos de la jornada activa)
    Promise.all([obtenerUltimaJornada(routeIdLiga), obtenerTotalJornadas(routeIdLiga)])
      .then(async ([ultimaJornadaRes, totalJornadasRes]) => {
        if (cancelada) {
          return;
        }
        const numUltima = parseInt(ultimaJornadaRes, 10) || 1;
        const numTotal = parseInt(totalJornadasRes, 10) || numUltima || 1;
        const partidosRes = await obtenerPartidosJornada(routeIdLiga, numUltima);
        if (!cancelada) {
          setUltimaJornada(numUltima);
          setTotalJornadas(numTotal);
          setPartidosJornada(partidosRes || []);
          setLoadingJornada(false);
          setLigaCacheItem(routeIdLiga, {
            ultimaJornada: numUltima,
            totalJornadas: numTotal,
            partidosJornada: partidosRes || [],
          });
        }
      })
      .catch((err) => {
        console.error('Error al cargar jornada:', err);
        if (!cancelada) {
          setLoadingJornada(false);
        }
      });

    // 4. Máximos Goleadores
    obtenerMaximosGoleadores(routeIdLiga)
      .then((res) => {
        if (!cancelada && res) {
          setMaximosGoleadores(res);
          setLoadingGoleadores(false);
          setLigaCacheItem(routeIdLiga, { maximosGoleadores: res });
        }
      })
      .catch((err) => {
        console.error('Error al cargar máximos goleadores:', err);
        if (!cancelada) {
          setLoadingGoleadores(false);
        }
      });

    // 5. Jugadores Promocionados
    obtenerJugadoresPromocionados(routeIdLiga)
      .then((res) => {
        if (!cancelada && res) {
          setJugadoresPromocionados(res);
          setLoadingPromocionados(false);
          setLigaCacheItem(routeIdLiga, { jugadoresPromocionados: res });
        }
      })
      .catch((err) => {
        console.error('Error al cargar jugadores promocionados:', err);
        if (!cancelada) {
          setLoadingPromocionados(false);
        }
      });

    // 6. Jugadores Guardados
    obtenerJugadoresGuardados(routeIdLiga)
      .then((res) => {
        if (!cancelada && res) {
          setJugadoresGuardados(res);
          setLoadingGuardados(false);
          setLigaCacheItem(routeIdLiga, { jugadoresGuardados: res });
        }
      })
      .catch((err) => {
        console.error('Error al cargar jugadores guardados:', err);
        if (!cancelada) {
          setLoadingGuardados(false);
        }
      });

    return () => {
      cancelada = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeIdLiga]);

  const handleObservacionUpdatePromocionados = (idJugador, updatedObservaciones) => {
    setJugadoresPromocionados((prev) => {
      const updated = prev
        ? prev.map((jugador) =>
            jugador.idJugador === idJugador ? { ...jugador, observaciones: updatedObservaciones } : jugador
          )
        : [];
      setLigaCacheItem(routeIdLiga, { jugadoresPromocionados: updated });
      return updated;
    });
  };

  const handleObservacionUpdateGuardados = (idJugador, updatedObservaciones) => {
    setJugadoresGuardados((prev) => {
      const updated = prev
        ? prev.map((jugador) =>
            jugador.idJugador === idJugador ? { ...jugador, observaciones: updatedObservaciones } : jugador
          )
        : [];
      setLigaCacheItem(routeIdLiga, { jugadoresGuardados: updated });
      return updated;
    });
  };

  return (
    <ContainerApp maxWidth={false}>
      <Box mb={2}>
        {!datosLiga && loadingDatosLiga ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <BarraBuscarLiga datosLiga={datosLiga} />
        )}
      </Box>
      {/* Contenedor general */}
      <Grid container spacing={3}>
        {/* Columna Izquierda: Clasificación y Máximos Goleadores (~41.7%) */}
        <Grid item xs={12} md={5}>
          <Grid container spacing={2}>
            {/* Clasificación */}
            <Grid item xs={12}>
              <Clasificacion clasificacion={clasificacion} loading={loadingClasificacion} />
            </Grid>
            {/* Máximos goleadores */}
            <Grid item xs={12}>
              <MaximosGoleadores
                maximosGoleadores={maximosGoleadores}
                datosLiga={datosLiga}
                loading={loadingGoleadores}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Columna Derecha: Jornada, Jugadores Guardados y Jugadores Promocionados (~58.3%) */}
        <Grid item xs={12} md={7}>
          <Grid container spacing={2}>
            {/* Jornada */}
            <Grid item xs={12}>
              {loadingJornada || !partidosJornada ? (
                <PaperApp
                  sx={{
                    p: 4,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 200,
                  }}
                >
                  <CircularProgress size={32} />
                </PaperApp>
              ) : (
                <Jornada
                  key={`${routeIdLiga}-${ultimaJornada}`}
                  datosLiga={datosLiga}
                  partidosJornada={partidosJornada}
                  jornadaInicial={ultimaJornada}
                  totalJornadas={totalJornadas}
                  ultimaJornada={ultimaJornada}
                />
              )}
            </Grid>
            {/* Jugadores Guardados */}
            <Grid item xs={12}>
              <ListaJugadoresLiga
                nombreLista={'Jugadores Guardados'}
                datosLiga={datosLiga}
                jugadores={jugadoresGuardados}
                loading={loadingGuardados}
                onUpdate={handleObservacionUpdateGuardados}
              />
            </Grid>
            {/* Jugadores Promocionados */}
            <Grid item xs={12}>
              <ListaJugadoresLiga
                nombreLista={'Jugadores Promocionados'}
                datosLiga={datosLiga}
                jugadores={jugadoresPromocionados}
                loading={loadingPromocionados}
                onUpdate={handleObservacionUpdatePromocionados}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ContainerApp>
  );
};

export default Liga;
